// CB:BEGIN admin-finance-page
/**
 * Admin Finance: escrows, ledger, invoices, reconcile. "Where is the money for project X?"
 * Header is provided by AdminDashboard; do not render a second one (matches admin-homeowners structure).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '../../lib/useApi';

interface EscrowRow {
  id: string;
  status: string;
  totalAmountCents: number;
  projectId: string;
  createdAt: string;
  project?: { id: string; address: string; status: string };
  bid?: { id: string; amount: number };
  homeowner?: { id: string; firstName: string | null; lastName: string | null };
  contractor?: { id: string; company: string | null };
}

interface LedgerEntryRow {
  id: string;
  type: string;
  amountCents: number;
  currency: string;
  createdAt: string;
  metadata?: unknown;
}

interface InvoiceRow {
  id: string;
  status: string;
  issuedAt: string;
  pdfUrl: string | null;
  escrow?: { id: string; projectId: string; totalAmountCents: number; project?: { address: string } };
}

// CB:BEGIN admin-finance-legacy-escrow-accounts
interface LegacyEscrowAccountRow {
  id: string;
  projectId: string;
  totalDeposited: number;
  currentBalance: number;
  totalReleased: number;
  status: string;
  createdAt: string;
  lastPaymentDate: string | null;
  project?: { id: string; address: string | null; status: string };
}
// CB:END admin-finance-legacy-escrow-accounts

interface AdminFinancePageProps {
  onNavigate?: (screen: string) => void;
}

const AdminFinancePage: React.FC<AdminFinancePageProps> = ({ onNavigate }) => {
  const api = useApi();
  const [escrows, setEscrows] = useState<EscrowRow[]>([]);
  const [legacyEscrowAccounts, setLegacyEscrowAccounts] = useState<LegacyEscrowAccountRow[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [ledger, setLedger] = useState<LedgerEntryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [projectIdFilter, setProjectIdFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedEscrowId, setSelectedEscrowId] = useState<string | null>(null);
  const [freezeReason, setFreezeReason] = useState('');
  const [freezeModalEscrowId, setFreezeModalEscrowId] = useState<string | null>(null);
  const [freezeSubmitting, setFreezeSubmitting] = useState(false);

  // CB:BEGIN finance-single-fetch
  const hasFetchedInitialData = useRef(false);
  const financeFetchCancelledRef = useRef(false);

  const fetchAllData = useCallback(async (cancelledRef?: { current: boolean }) => {
    setError(null);
    setLoading(true);
    const isCancelled = () => cancelledRef?.current === true;
    try {
      const escrowParams = new URLSearchParams();
      if (statusFilter) escrowParams.set('status', statusFilter);
      if (projectIdFilter) escrowParams.set('projectId', projectIdFilter);
      if (fromDate) escrowParams.set('fromDate', fromDate);
      if (toDate) escrowParams.set('toDate', toDate);
      escrowParams.set('limit', '100');
      const escrowData = await api.get<EscrowRow[]>(`/api/admin/finance/escrows?${escrowParams}`);
      if (!isCancelled()) setEscrows(Array.isArray(escrowData) ? escrowData : []);
    } catch (e) {
      if (!isCancelled()) {
        setError(e instanceof Error ? e.message : 'Failed to load escrows');
        setEscrows([]);
      }
    }
    try {
      const legacyParams = new URLSearchParams();
      if (projectIdFilter) legacyParams.set('projectId', projectIdFilter);
      legacyParams.set('limit', '100');
      const legacyData = await api.get<LegacyEscrowAccountRow[]>(`/api/admin/finance/escrow-accounts?${legacyParams}`);
      if (!isCancelled()) setLegacyEscrowAccounts(Array.isArray(legacyData) ? legacyData : []);
    } catch {
      if (!isCancelled()) setLegacyEscrowAccounts([]);
    }
    try {
      const invoiceParams = new URLSearchParams();
      if (projectIdFilter) invoiceParams.set('projectId', projectIdFilter);
      invoiceParams.set('limit', '100');
      const invoiceData = await api.get<InvoiceRow[]>(`/api/admin/finance/invoices?${invoiceParams}`);
      if (!isCancelled()) setInvoices(Array.isArray(invoiceData) ? invoiceData : []);
    } catch {
      if (!isCancelled()) setInvoices([]);
    }
    if (!isCancelled()) setLoading(false);
  }, [statusFilter, projectIdFilter, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps
  // CB:END finance-single-fetch

  const fetchLedger = useCallback(async (escrowId: string) => {
    try {
      const data = await api.get<LedgerEntryRow[]>(`/api/admin/finance/escrows/${escrowId}/ledger`);
      setLedger(Array.isArray(data) ? data : []);
    } catch {
      setLedger([]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasFetchedInitialData.current) {
      hasFetchedInitialData.current = true;
      financeFetchCancelledRef.current = false;
      fetchAllData(financeFetchCancelledRef);
      return () => { financeFetchCancelledRef.current = true; };
    }
  }, [fetchAllData]);

  useEffect(() => {
    if (selectedEscrowId) fetchLedger(selectedEscrowId);
    else setLedger([]);
  }, [selectedEscrowId, fetchLedger]);

  const handleFreeze = async () => {
    if (!freezeModalEscrowId) return;
    setFreezeSubmitting(true);
    try {
      await api.post(`/api/admin/finance/escrows/${freezeModalEscrowId}/freeze`, { reason: freezeReason || 'Admin freeze' });
      setFreezeModalEscrowId(null);
      setFreezeReason('');
      await fetchAllData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Freeze failed');
    } finally {
      setFreezeSubmitting(false);
    }
  };

  const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const formatDate = (s: string) => new Date(s).toLocaleDateString();

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
        <h1 style={{ fontSize: 28, marginBottom: 8 }} data-testid="admin-finance-title">Finance: Escrow & Ledger</h1>
        <p style={{ color: '#6b7280', marginBottom: 24 }}>Inspect escrows, ledger entries, and invoices. Reconcile and freeze (DISPUTED) with reason.</p>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24, alignItems: 'flex-end' }}>
          <label>
            Status
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ marginLeft: 8, padding: 8 }}
            >
              <option value="">All</option>
              <option value="CREATED">CREATED</option>
              <option value="FUNDED">FUNDED</option>
              <option value="LOCKED">LOCKED</option>
              <option value="RELEASE_PENDING">RELEASE_PENDING</option>
              <option value="RELEASED">RELEASED</option>
              <option value="REFUND_PENDING">REFUND_PENDING</option>
              <option value="REFUNDED">REFUNDED</option>
              <option value="DISPUTED">DISPUTED</option>
            </select>
          </label>
          <label>
            Project ID (where is the money?)
            <input
              type="text"
              value={projectIdFilter}
              onChange={(e) => setProjectIdFilter(e.target.value)}
              placeholder="Filter by project"
              style={{ marginLeft: 8, padding: 8, width: 220 }}
            />
          </label>
          <label>
            From
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ marginLeft: 8, padding: 8 }} />
          </label>
          <label>
            To
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ marginLeft: 8, padding: 8 }} />
          </label>
          <button type="button" onClick={fetchAllData} style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6 }}>
            Apply
          </button>
        </div>

        {error && <div style={{ color: '#dc2626', marginBottom: 16 }}>{error}</div>}
        {loading && <div style={{ marginBottom: 16 }}>Loading...</div>}

        {/* Escrows table */}
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 20, marginBottom: 12 }}>Escrows</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: 12, textAlign: 'left' }}>Escrow ID</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Project</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Status</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>Total</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Created</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {escrows.map((e) => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: 12, fontFamily: 'monospace', fontSize: 12 }}>{e.id.slice(0, 12)}…</td>
                    <td style={{ padding: 12 }}>{e.project?.address ?? e.projectId}</td>
                    <td style={{ padding: 12 }}>{e.status}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{formatCents(e.totalAmountCents)}</td>
                    <td style={{ padding: 12 }}>{formatDate(e.createdAt)}</td>
                    <td style={{ padding: 12 }}>
                      <button type="button" onClick={() => setSelectedEscrowId(selectedEscrowId === e.id ? null : e.id)} style={{ marginRight: 8, padding: '4px 8px' }}>
                        {selectedEscrowId === e.id ? 'Hide ledger' : 'Ledger'}
                      </button>
                      {e.status !== 'DISPUTED' && e.status !== 'RELEASED' && e.status !== 'REFUNDED' && (
                        <button type="button" onClick={() => setFreezeModalEscrowId(e.id)} style={{ padding: '4px 8px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 4 }}>
                          Freeze
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Escrow accounts (legacy — where deposited money lives, e.g. $12k in) */}
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 20, marginBottom: 12 }}>Escrow accounts (where the money is)</h2>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 12 }}>Per-project balances: total deposited and current balance. Use Project ID filter above to find a specific project.</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: 12, textAlign: 'left' }}>Account ID</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Project</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Status</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>Total deposited</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>Current balance</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>Total released</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Last payment</th>
                </tr>
              </thead>
              <tbody>
                {legacyEscrowAccounts.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: 12, fontFamily: 'monospace', fontSize: 12 }}>{a.id.slice(0, 12)}…</td>
                    <td style={{ padding: 12 }}>{a.project?.address ?? a.projectId}</td>
                    <td style={{ padding: 12 }}>{a.status}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{formatCents(a.totalDeposited)}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{formatCents(a.currentBalance)}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{formatCents(a.totalReleased)}</td>
                    <td style={{ padding: 12 }}>{a.lastPaymentDate ? formatDate(a.lastPaymentDate) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {legacyEscrowAccounts.length === 0 && !loading && (
            <p style={{ color: '#6b7280', padding: 12 }}>No escrow accounts match the filter.</p>
          )}
        </section>

        {/* Ledger drilldown */}
        {selectedEscrowId && (
          <section style={{ marginBottom: 32, padding: 16, background: '#f9fafb', borderRadius: 8 }}>
            <h3 style={{ marginBottom: 12 }}>Ledger (sorted by createdAt)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Type</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ padding: 8 }}>{entry.type}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{formatCents(entry.amountCents)}</td>
                    <td style={{ padding: 8 }}>{formatDate(entry.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Invoices */}
        <section>
          <h2 style={{ fontSize: 20, marginBottom: 12 }}>Invoices</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: 12, textAlign: 'left' }}>Invoice ID</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Project</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Status</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Issued</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: 12, fontFamily: 'monospace', fontSize: 12 }}>{inv.id.slice(0, 12)}…</td>
                    <td style={{ padding: 12 }}>{inv.escrow?.project?.address ?? inv.escrow?.projectId ?? '—'}</td>
                    <td style={{ padding: 12 }}>{inv.status}</td>
                    <td style={{ padding: 12 }}>{formatDate(inv.issuedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

      {/* Freeze modal */}
      {freezeModalEscrowId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: 'white', padding: 24, borderRadius: 8, maxWidth: 400 }}>
            <h3>Freeze escrow (DISPUTED)</h3>
            <p style={{ color: '#6b7280', marginBottom: 16 }}>This will transition the escrow to DISPUTED. Provide a reason (logged in audit).</p>
            <textarea value={freezeReason} onChange={(e) => setFreezeReason(e.target.value)} placeholder="Reason" rows={3} style={{ width: '100%', padding: 8, marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setFreezeModalEscrowId(null); setFreezeReason(''); }} disabled={freezeSubmitting}>Cancel</button>
              <button type="button" onClick={handleFreeze} disabled={freezeSubmitting} style={{ background: '#b91c1c', color: 'white', padding: '8px 16px', border: 'none', borderRadius: 6 }}>Freeze</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFinancePage;
// CB:END admin-finance-page
